import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import {
  GetUsersApiResponse,
  USERS_QUERY_KEY,
  UsersPaginatedDataModel,
  UsersPaginatedFilters,
  UserWithHotel,
} from './types';

// Stronicowana lista użytkowników panelu — czytana z backendu (v3-getUsers, admin SDK),
// bo kolekcja `users` nie jest już czytana client-side. Backend robi też join nazw hoteli.
export const useGetUsers = (
  page: number,
  pageSize: number,
  filters?: UsersPaginatedFilters,
  options?: UseQueryOptions<UsersPaginatedDataModel, AxiosError>,
): UseQueryResult<UsersPaginatedDataModel, AxiosError> => useQuery<
  UsersPaginatedDataModel,
  AxiosError
>({
  queryKey: [
    USERS_QUERY_KEY,
    'paginated',
    page,
    pageSize,
    filters?.hotelId ?? '',
    (filters?.searchTerm ?? '').trim().toLowerCase(),
  ],
  queryFn: async (): Promise<UsersPaginatedDataModel> => {
    const res = await axiosInstance.get<GetUsersApiResponse>('/v3-getUsers', {
      params: {
        page,
        perPage: pageSize,
        search: (filters?.searchTerm ?? '').trim() || undefined,
        hotelId: filters?.hotelId || undefined,
      },
    });

    const payload = res.data.data;
    return { data: payload.users, totalPages: payload.totalPages, total: payload.total };
  },
  staleTime: 1000 * 60 * 2,
  retry: 1,
  ...options,
});

// Pełna lista użytkowników (bez stronicowania) — UserSelector wybiera z całości.
export const useGetAllUsers = (
  options?: UseQueryOptions<UserWithHotel[], AxiosError>,
): UseQueryResult<UserWithHotel[], AxiosError> => useQuery<UserWithHotel[], AxiosError>({
  queryKey: [USERS_QUERY_KEY, 'all'],
  queryFn: async (): Promise<UserWithHotel[]> => {
    const res = await axiosInstance.get<GetUsersApiResponse>('/v3-getUsers', {
      params: { all: true },
    });
    return res.data.data.users;
  },
  staleTime: 1000 * 60 * 5,
  retry: 1,
  ...options,
});
